import { Fragment, useEffect, useMemo, useState } from 'react'

import { Skeleton } from '@mui/material'
import { useCommercialStructure } from 'commons/customHooks'
import { formatDate } from 'commons/utils/date'
import { numberToDecimalString } from 'commons/utils/number'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import {
  BarGraph,
  GoalGraph,
  GraphLegends,
  Icon,
  Info,
  LoadingHavaianas,
  MultiselectMenu,
  SelectMenu,
  SlimButton,
  Tooltip,
  Typography,
} from 'components'
import type { Channels, Offices } from 'constants/enums'
import type { StrategyProduct } from 'services/home/interfaces'
import type { UserPreferences } from 'services/login/interfaces'
import {
  getCategoryReport,
  getDashboard,
  getFilterOptions,
  getMetaReport,
  getQuotaMonthOptions,
  setSelectedQuota,
} from 'store/HomeContext/homeSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { shallowEqual, useDispatch, useSelector } from 'store/store'

import * as S from './styles'

export const Dashboard = () => {
  const dispatch = useDispatch()

  const {
    loadingDashboard,
    loadingOptions,
    loadingMetaReport,
    loadingCategoryReport,
    loadingQuotaOptions,
    selectedQuota,
    quotaOptions,
    dashboardData,
  } = useSelector((state) => state.home, shallowEqual)

  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure,
  } = useSelector((state) => state.shared)

  const { getMarkets, getOfficesByMarkets, getRegionalsByOffices, getZonesByRegionals } =
    useCommercialStructure()

  const legends = [
    { fill: '#FF4842', label: 'Até 49%' },
    { fill: '#FF8D23', label: 'De 50% até 84%' },
    { fill: '#FFC107', label: 'De 85% até 99%' },
    { fill: '#54D62C', label: 'Acima de 100%' },
  ]

  const materialPrefix = 'SANDALIAS HAVAIANAS '

  const categoryLabelsMap: Record<string, string> = {
    BRANDEXTENSION: 'Brand Extension',
    ESTRATEGICO: 'Estratégico',
    FEMININA: 'Feminina',
    'KIDS&BABY': 'Kids e Baby',
    MASCULINA: 'Masculina',
    OLIMPÍADAS: 'Olimpíadas',
    PHASEOUT: 'Phase Out',
    SELEÇÃOESPECIAL: 'Seleção Especial',
    UNISSEX: 'Unissex',
  }

  const calcPercent = (value: number, goal: number) => {
    if (goal > 0) return (value / goal) * 100
    return 0
  }

  useEffect(() => {
    dispatch(getCommercialStructure())
    dispatch(getFilterOptions())
  }, [])

  const metaPercent = useMemo(() => {
    const { VolumeTotal, VolumeAprovado } = dashboardData.CarteiraTotal
    return calcPercent(VolumeAprovado, VolumeTotal)
  }, [dashboardData])

  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([])
  const [selectedOffices, setSelectedOffices] = useState<string[]>([])
  const [selectedRegionals, setSelectedRegionals] = useState<string[]>([])
  const [selectedZones, setSelectedZones] = useState<string[]>([])

  const preferences = JSON.parse(localStorage.getItem('preferences') || '{}') as UserPreferences
  const {
    CanalDistribuicao: prefMarkets,
    EscritorioVenda: prefOffices,
    EquipeVenda: prefRegionals,
    RegiaoVenda: prefZones,
  } = preferences

  const disableSearch = useMemo(() => {
    return (
      selectedMarkets.length === 0 ||
      selectedOffices.length === 0 ||
      selectedRegionals.length === 0 ||
      selectedZones.length === 0 ||
      loadingDashboard ||
      loadingOptions
    )
  }, [
    selectedMarkets,
    selectedOffices,
    selectedRegionals,
    selectedZones,
    loadingDashboard,
    loadingOptions,
  ])

  const restoreCommercialStructure = () => {
    setSelectedMarkets(prefMarkets ? [prefMarkets] : markets.map((market) => market.id))
    setSelectedOffices(prefOffices ? [prefOffices] : offices.map((office) => office.id))
    setSelectedRegionals(prefRegionals ? [prefRegionals] : regionals.map((regional) => regional.id))
    setSelectedZones(prefZones ? [prefZones] : zones.map((zone) => zone.id))
  }

  useEffect(() => {
    restoreCommercialStructure()
  }, [officesCommercialStructure, regionalsCommercialStructure, zonesCommercialStructure])

  const markets = useMemo(() => {
    return getMarkets()
  }, [selectedMarkets, officesCommercialStructure])

  const offices = useMemo(() => {
    const officesByMarket = getOfficesByMarkets(selectedMarkets as Channels[])

    if (selectedMarkets.length && prefMarkets) {
      const asSameAsPrefMarket = selectedMarkets.every((market) => [prefMarkets].includes(market))

      if (asSameAsPrefMarket && prefOffices) {
        setSelectedOffices([prefOffices] as string[])
        return officesByMarket
      }
    }

    setSelectedOffices(officesByMarket.map((office) => office.id))

    return officesByMarket
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    const regionalsByOffices = getRegionalsByOffices(selectedOffices as Offices[])

    if (selectedOffices.length && prefOffices) {
      const asSameAsPrefOffice = selectedOffices.every((office) => [prefOffices].includes(office))

      if (asSameAsPrefOffice && prefRegionals) {
        setSelectedRegionals([prefRegionals] as string[])
        return regionalsByOffices
      }
    }

    setSelectedRegionals(regionalsByOffices.map((regional) => regional.id))

    return regionalsByOffices
  }, [selectedOffices, regionalsCommercialStructure])

  const zones = useMemo(() => {
    const zonesByRegionals = getZonesByRegionals(selectedRegionals)

    if (selectedRegionals.length && prefRegionals) {
      const asSameAsPrefRegional = selectedRegionals.every((regional) =>
        [prefRegionals].includes(regional),
      )

      if (asSameAsPrefRegional && prefZones) {
        setSelectedZones([prefZones] as string[])
        return zonesByRegionals
      }
    }

    setSelectedZones(zonesByRegionals.map((zone) => zone.id))

    return zonesByRegionals
  }, [selectedRegionals, zonesCommercialStructure])

  const handleQuotaSelect = (value: string) => {
    dispatch(setSelectedQuota(value))
  }

  const handleOfficeChange = (value: string[]) => {
    setSelectedOffices(value)

    if (value.length) {
      dispatch(getQuotaMonthOptions(value))
    }
  }

  const formattedQuota = formatDate(selectedQuota, 'YYYYMM', 'MM/YY')

  const handleMetaReport = () => {
    dispatch(
      getMetaReport(
        {
          AnoMesCota: formattedQuota,
          EscritorioVenda: selectedOffices,
          Regional: selectedRegionals,
          Zona: selectedZones,
        },
        regionals.length,
        zones.length,
      ),
    )
  }

  const handleCategoryReport = () => {
    dispatch(
      getCategoryReport(
        {
          AnoMesCota: formattedQuota,
          EscritorioVenda: selectedOffices,
          Regional: selectedRegionals,
          Zona: selectedZones,
        },
        regionals.length,
        zones.length,
      ),
    )
  }

  const handleSearch = () => {
    dispatch(
      getDashboard({
        AnoMesCota: formattedQuota,
        EscritorioVenda: selectedOffices,
        Regional: selectedRegionals.length === regionals.length ? [] : selectedRegionals,
        Zona: selectedZones.length === zones.length ? [] : selectedZones,
      }),
    )
  }

  const handleClear = () => {
    dispatch(getFilterOptions())
    restoreCommercialStructure()
  }

  const renderSkeleton = () => {
    const items = Array.from({ length: 5 }, (_, index) => ({
      id: index + 1,
    }))

    return items.map((item, index) => {
      const shouldRenderDivider = index !== 0

      return (
        <Fragment key={`${item.id}`}>
          {shouldRenderDivider && <S.VertDivider />}
          <S.DataCol>
            <Skeleton width={80} />
            <S.DataNumber>
              <Skeleton width={40} />
              <Skeleton width={60} />
            </S.DataNumber>
          </S.DataCol>
        </Fragment>
      )
    })
  }

  const products = dashboardData.ProdutosEstrategicos

  const renderStrategyItems = (products: StrategyProduct[]) => {
    return products.map((product) => {
      return (
        <S.DataCol className="grid-item" key={`${product.Codigo}`}>
          <S.DataNumber>
            <Typography design="hav-h8-text">
              {toAllFirstLetterUpper(product.Descricao.replace(materialPrefix, ''))}
            </Typography>
            <Typography design="hav-default-05-text">Código: {product.Codigo}</Typography>
          </S.DataNumber>
          <span>
            <Typography design="hav-h7-text">
              {numberToDecimalString(product.VolumeAprovado)}{' '}
            </Typography>
            <Typography design="hav-default-04-text">
              / {numberToDecimalString(product.VolumeTotal)}
            </Typography>
          </span>
          <BarGraph percentValue={calcPercent(product.VolumeAprovado, product.VolumeTotal)} />
        </S.DataCol>
      )
    })
  }

  return (
    <S.Wrapper>
      <S.TitleCard>
        <S.TitleRow>
          <Typography design="hav-h5-text">Gestão da carteira</Typography>
          <S.FiltersRow>
            <MultiselectMenu
              checkAllAction={() => setSelectedMarkets(markets.map((market) => market.id))}
              clearAction={() => {
                setSelectedMarkets([])
                handleOfficeChange([])
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              disabled={loadingOptions || loadingDashboard}
              hasBorder
              label="Mercado"
              onChange={(value) => {
                setSelectedMarkets(value)
              }}
              optionsList={markets.map((market) => ({
                label: market.description,
                value: market.id,
              }))}
              required
              selectedOptions={selectedMarkets}
            />
            <MultiselectMenu
              checkAllAction={() => handleOfficeChange(offices.map((office) => office.id))}
              clearAction={() => {
                handleOfficeChange([])
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              disabled={loadingOptions || loadingDashboard}
              hasBorder
              label="Escritório"
              onChange={(value) => {
                handleOfficeChange(value)
              }}
              optionsList={offices.map((office) => ({
                label: `${office.id} - ${office.description}`,
                value: office.id,
              }))}
              required
              selectedOptions={selectedOffices}
            />
            <MultiselectMenu
              checkAllAction={() => setSelectedRegionals(regionals.map((regional) => regional.id))}
              clearAction={() => {
                setSelectedRegionals([])
                setSelectedZones([])
              }}
              disabled={loadingOptions || loadingDashboard}
              hasBorder
              hasSearch
              label="Regional"
              onChange={(value) => {
                setSelectedRegionals(value)
              }}
              optionsList={regionals.map((regional) => ({
                label: `${regional.id} - ${regional.description}`,
                value: regional.id,
              }))}
              required
              searchPlaceholder="Buscar regional"
              selectedOptions={selectedRegionals}
            />
            <MultiselectMenu
              checkAllAction={() => setSelectedZones(zones.map((zone) => zone.id))}
              clearAction={() => setSelectedZones([])}
              disabled={loadingOptions || loadingDashboard}
              hasBorder
              hasSearch
              label="Zona"
              onChange={(value) => {
                setSelectedZones(value)
              }}
              optionsList={zones.map((zone) => ({
                label: `${zone.id} - ${zone.description}`,
                value: zone.id,
              }))}
              required
              searchPlaceholder="Buscar zona"
              selectedOptions={selectedZones}
            />
            <SelectMenu
              disabled={loadingOptions || loadingDashboard || loadingQuotaOptions}
              hasBorder
              label="Mês cota"
              onChange={handleQuotaSelect}
              options={quotaOptions}
              value={selectedQuota}
            />
            <SlimButton
              content="Buscar"
              disabled={disableSearch || loadingQuotaOptions}
              onClick={handleSearch}
              variant="primary"
            />
            <Tooltip arrow placement="top" title="Redefinir filtros">
              <div>
                <SlimButton
                  disabled={loadingOptions || loadingDashboard || loadingQuotaOptions}
                  icon={<Icon icon="clear-all" size="20" />}
                  id="home-filters-clear-button"
                  onClick={handleClear}
                  variant="secondary"
                />
              </div>
            </Tooltip>
          </S.FiltersRow>
        </S.TitleRow>
      </S.TitleCard>
      <S.Content>
        <S.SectionCard className="carteira">
          <S.TitleRow>
            <S.TitleCol>
              <Typography design="hav-h6-text">Saldo para Dispo</Typography>
              <Typography design="hav-default-02-disabled">
                Baixe e veja o que ainda tem para vender.
              </Typography>
              <S.WrapDetailsButton>
                <SlimButton
                  content="Baixar"
                  icon={<Icon icon="download" size="15" />}
                  loading={loadingMetaReport}
                  onClick={handleMetaReport}
                  variant={'info'}
                />
              </S.WrapDetailsButton>
            </S.TitleCol>
          </S.TitleRow>
          <S.RowDivider />
          <S.TitleRow>
            <S.SectionTitle>
              <Typography design="hav-h6-text">Carteira total & Meta</Typography>
              <Info iconSize={15}>Acompanhe o seu desempenho em relação à meta de vendas.</Info>
            </S.SectionTitle>
          </S.TitleRow>
          <S.ResultsWrapper>
            {loadingDashboard ? (
              <S.GraphLoading>
                <LoadingHavaianas loadingText="" />
              </S.GraphLoading>
            ) : (
              <S.GraphRow>
                <GoalGraph percentValue={metaPercent} />
                <GraphLegends data={legends} />
              </S.GraphRow>
            )}
            <S.ResultsCol>
              <S.HozDivider />
              <S.TextRow>
                <Typography design="hav-h8-text">Volume da meta</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={75} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeTotal)
                  )}
                </Typography>
              </S.TextRow>
              <S.HozDivider />
              <S.TextRow>
                <Typography design="hav-h8-text">Aprovados</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={75} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeAprovado)
                  )}
                </Typography>
              </S.TextRow>
              <S.TextRow>
                <Typography design="hav-h8-text">Digitados</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={50} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeDigitado)
                  )}
                </Typography>
              </S.TextRow>
              <S.TextRow>
                <Typography design="hav-h8-text">Meta restante</Typography>
                <Typography design="hav-h7-text">
                  {loadingDashboard ? (
                    <Skeleton width={60} />
                  ) : (
                    numberToDecimalString(dashboardData.CarteiraTotal.VolumeRestante)
                  )}
                </Typography>
              </S.TextRow>
            </S.ResultsCol>
          </S.ResultsWrapper>
        </S.SectionCard>

        <S.SectionCard className="cestas">
          <S.TitleCol>
            <S.TitleRow>
              <S.SectionTitle>
                <Typography design="hav-h6-text">Acompanhamento por categoria</Typography>
                <Info iconSize={15}>
                  Categorias de produtos que compõem a sua meta. Essa segmentação é usada para
                  calcular a remuneração variável e garantir a diversidade de linhas dentro da
                  carteira.
                </Info>
              </S.SectionTitle>
              {loadingDashboard ? (
                <Skeleton width={150} />
              ) : (
                <S.ActionLink loading={loadingCategoryReport}>
                  <span onClick={handleCategoryReport}>Ver composição de cestas</span>
                </S.ActionLink>
              )}
            </S.TitleRow>
            <Typography design="hav-default-04-text">Valores exibidos em pares</Typography>
          </S.TitleCol>
          <S.DataRow>
            {loadingDashboard
              ? renderSkeleton()
              : dashboardData.CestasDeProdutos.map((category, index) => {
                  return (
                    <Fragment key={`${category.Nome}`}>
                      {index !== 0 && <S.VertDivider />}
                      <S.DataCol>
                        <Typography design="hav-h8-text">
                          {categoryLabelsMap[category.Nome]}
                        </Typography>
                        <span>
                          <Typography design="hav-h7-text">
                            {numberToDecimalString(category.VolumeAprovado)}{' '}
                          </Typography>
                          <Typography design="hav-default-04-text">
                            / {numberToDecimalString(category.VolumeTotal)}
                          </Typography>
                        </span>
                        <BarGraph
                          percentValue={calcPercent(category.VolumeAprovado, category.VolumeTotal)}
                        />
                      </S.DataCol>
                    </Fragment>
                  )
                })}
          </S.DataRow>
        </S.SectionCard>

        <S.SectionCard className="produtos">
          <S.TitleCol>
            <S.SectionTitle>
              <Typography design="hav-h6-text">Produtos estratégicos & Meta</Typography>
              <Info iconSize={15}>
                Produtos estratégicos com maior potencial de margem e grande oferta. O volume
                estratégico aprovado dentro da sua carteira compõe a apuração da remuneração
                variável.
              </Info>
            </S.SectionTitle>

            <Typography design="hav-default-04-text">Valores exibidos em pares</Typography>
          </S.TitleCol>
          {loadingDashboard ? (
            <S.DataRow>{renderSkeleton()}</S.DataRow>
          ) : (
            <S.StrategyGrid>{renderStrategyItems(products)}</S.StrategyGrid>
          )}
        </S.SectionCard>
      </S.Content>
    </S.Wrapper>
  )
}
